﻿import { motion } from "framer-motion";
import { Link } from "wouter";
import { useState } from "react";
import { 
  FaFacebook, 
  FaTwitter, 
  FaLinkedin, 
  FaInstagram,
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt,
  FaRocket,
  FaPaperPlane,
  FaHeart,
  FaArrowRight
} from "react-icons/fa";
import { aiServices } from "@/lib/aiServices";
import { getSeoContent } from "@/lib/seoContent";
import { useLocation } from "wouter";

function RouteSeoContent() {
  const [location] = useLocation();
  const pathname = location.split("?")[0] || "/";
  const content = getSeoContent(pathname);

  if (!content || pathname === "/services/software-development") return null;

  return (
    <section className="bg-white py-16 text-slate-900 dark:bg-slate-950 dark:text-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
          <div>
            <p className="text-sm font-bold uppercase tracking-wide text-blue-600 dark:text-blue-300">
              {content.eyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-extrabold leading-tight sm:text-4xl">
              {content.heading}
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-slate-600 dark:text-slate-300">
              {content.intro}
            </p>
            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
              <h3 className="text-lg font-bold">Buyer problem</h3>
              <p className="mt-3 leading-relaxed text-slate-600 dark:text-slate-300">
                {content.problem}
              </p>
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-white/5">
              <h3 className="font-bold">What we build</h3>
              <ul className="mt-4 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                {content.builds.map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-blue-600" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-white/5">
              <h3 className="font-bold">Industries and use cases</h3>
              <ul className="mt-4 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                {content.industries.map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-cyan-500" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
            <h3 className="font-bold">Technology stack</h3>
            <div className="mt-4 flex flex-wrap gap-2">
              {content.stack.map((item) => (
                <span key={item} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 dark:border-white/10 dark:bg-white/10 dark:text-slate-200">
                  {item}
                </span>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
            <h3 className="font-bold">Delivery process</h3>
            <ol className="mt-4 space-y-2 text-sm text-slate-600 dark:text-slate-300">
              {content.process.map((item, index) => (
                <li key={item} className="flex gap-2">
                  <span className="font-bold text-blue-600 dark:text-blue-300">{index + 1}.</span>
                  <span>{item}</span>
                </li>
              ))}
            </ol>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
            <h3 className="font-bold">Why Variable Soft</h3>
            <ul className="mt-4 space-y-2 text-sm text-slate-600 dark:text-slate-300">
              {content.reasons.map((item) => (
                <li key={item} className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-indigo-500" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[1fr_0.8fr]">
          <div>
            <h3 className="text-2xl font-extrabold">FAQs</h3>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              {content.faqs.map((faq) => (
                <div key={faq.question} className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
                  <h4 className="font-bold">{faq.question}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-blue-100 bg-blue-50 p-6 dark:border-blue-400/20 dark:bg-blue-400/10">
            <h3 className="text-xl font-extrabold">Related pages</h3>
            <div className="mt-5 flex flex-wrap gap-3">
              {content.relatedLinks.map((link) => (
                <a key={link.href} href={link.href} className="rounded-full border border-blue-200 bg-white px-4 py-2 text-sm font-semibold text-blue-700 transition hover:border-blue-500 dark:border-white/10 dark:bg-white/10 dark:text-blue-200">
                  {link.label}
                </a>
              ))}
            </div>
            <a href="/getintouch" className="mt-6 inline-flex rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700">
              Discuss Your Requirement
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function BreadcrumbTrail() {
  const [location] = useLocation();
  const pathname = location.split("?")[0] || "/";
  if (pathname === "/") return null;

  const parts = pathname.split("/").filter(Boolean);
  const crumbs = parts.map((part, index) => {
    const href = `/${parts.slice(0, index + 1).join("/")}`;
    const label = part
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    return { href, label };
  });

  return (
    <nav aria-label="Breadcrumb" className="bg-white px-4 py-4 text-sm text-slate-600 dark:bg-slate-950 dark:text-slate-300">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-2 sm:px-6 lg:px-8">
        <a href="/" className="font-semibold text-blue-700 hover:underline dark:text-blue-300">
          Home
        </a>
        {crumbs.map((crumb, index) => (
          <span key={crumb.href} className="flex items-center gap-2">
            <span aria-hidden="true" className="text-slate-400">/</span>
            {index === crumbs.length - 1 ? (
              <span className="font-semibold text-slate-900 dark:text-white">{crumb.label}</span>
            ) : (
              <a href={crumb.href} className="font-semibold text-blue-700 hover:underline dark:text-blue-300">
                {crumb.label}
              </a>
            )}
          </span>
        ))}
      </div>
    </nav>
  );
}

export default function Footer() {
  const [email, setEmail] = useState("");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const currentYear = new Date().getFullYear();

  const services = [
    { name: "Custom Software Development", href: "/services/software-development" },
    ...aiServices.slice(0, 3).map((service) => ({ name: service.shortTitle, href: service.href })),
    { name: "Web Application Development", href: "/software-development-in-jaipur" },
    { name: "Mobile App Development", href: "/top-software-companies-in-jaipur" },
    { name: "Ecommerce Development", href: "/ecommerce-development-services" },
    { name: "UI/UX & Web Design", href: "/webdesign" },
    { name: "SEO & Digital Growth", href: "/it-companies-in-jaipur" },
    { name: "WhatsApp Business API", href: "/services/whatsapp-business-api" },
    { name: "Maintenance & Support", href: "/maintenanceandsupport" },
    { name: "Micro-GCC Teams", href: "/services/micro-gcc-india" },
  ];

  const solutions = [
    { name: "CRM Software", href: "/crm" },
    { name: "CRM Features", href: "/crm/features" },
    { name: "ERP Software", href: "/products/erp" },
    { name: "IVR System", href: "/ivr-system" },
    { name: "Cloud Call Center", href: "/cloud-call-center" },
    { name: "Dialer Software", href: "/products/dialer" },
    { name: "Task Management", href: "/products/taskmanagement" },
    { name: "Hosting Services", href: "/products/hosting" },
    { name: "SSL Certificates", href: "/products/sslcertificate" },
  ];

  const company = [
    { name: "About Us", href: "/whoweare" },
    { name: "Portfolio", href: "/portfolio" },
    { name: "Careers", href: "/career" },
    { name: "Blog", href: "/blog" },
    { name: "Contact", href: "/getintouch" },
  ];

  const socialLinks = [
    { icon: <FaFacebook />, href: "https://www.facebook.com/variablesoft", color: "hover:text-blue-500" },
    { icon: <FaTwitter />, href: "https://x.com/variablesoft", color: "hover:text-sky-400" },
    { icon: <FaLinkedin />, href: "https://www.linkedin.com/company/variablesoft", color: "hover:text-blue-600" },
    { icon: <FaInstagram />, href: "https://www.instagram.com/variable_soft/", color: "hover:text-pink-500" },
  ];

  const techStack = ["React", "TypeScript", "AWS", "Python", "Node.js", "Next.js", "MongoDB", "Docker"];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Subscribed with email:", email);
    setIsSubscribed(true);
    setTimeout(() => setIsSubscribed(false), 3000);
    setEmail("");
  };

  // Floating particles for background
  const particles = Array.from({ length: 15 }).map((_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    delay: Math.random() * 5,
    size: Math.random() * 3 + 1,
  }));

  return (
    <>
    <BreadcrumbTrail />
    <RouteSeoContent />
    <footer className="relative bg-gradient-to-br from-gray-900 via-gray-950 to-black text-white overflow-hidden pb-10">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}
        />
        
        {/* Floating Particles */}
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            className="absolute rounded-full bg-blue-500/10"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
            }}
            animate={{
              y: [0, -30, 0],
              x: [0, Math.random() * 20 - 10, 0],
            }}
            transition={{
              duration: 3 + Math.random() * 4,
              repeat: Infinity,
              delay: particle.delay,
              ease: "easeInOut"
            }}
          />
        ))}
        
        {/* Gradient Orbs */}
        <motion.div
          className="absolute top-1/4 -left-32 w-64 h-64 rounded-full bg-gradient-to-r from-blue-900/20 to-purple-900/20 blur-3xl"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-1/4 -right-32 w-64 h-64 rounded-full bg-gradient-to-r from-purple-900/20 to-pink-900/20 blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      {/* Top Wave Divider */}
     <div className="relative z-10">
        {/* Main Footer Content */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-4">
          {/* Newsletter Section */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, type: "spring" }}
            viewport={{ once: true }}
            className="mb-16 lg:mb-20"
          >
           
          </motion.div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-16">
            {/* Company Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <motion.div
                  className="text-4xl font-bold mb-4 tracking-tight"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                    VariableSoft
                  </span>
                </motion.div>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  We craft innovative digital solutions to drive business growth and transform ideas into reality.
                </p>
                
                {/* Social Links */}
                <div className="flex gap-4">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-12 h-12 rounded-full bg-gray-800/50 backdrop-blur-sm flex items-center justify-center text-xl transition-all duration-300 ${social.color} hover:scale-110 border border-gray-700 hover:border-transparent`}
                      initial={{ opacity: 0, scale: 0 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.1 * index, type: "spring" }}
                      whileHover={{ y: -5 }}
                      viewport={{ once: true }}
                    >
                      {social.icon}
                    </motion.a>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Services */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h2 className="text-xl font-semibold mb-6 text-gray-200 pb-3 border-b border-gray-800 flex items-center gap-2">
                <div className="w-2 h-8 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full" />
                Our Services
              </h2>
              <ul className="space-y-3">
                {services.map((service, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.05 * index }}
                    whileHover={{ x: 5 }}
                    viewport={{ once: true }}
                    className="group"
                  >
                    <Link href={service.href}>
                      <span className="flex items-center gap-3 text-gray-400 hover:text-white transition-all duration-300 cursor-pointer py-2 group-hover:bg-gray-800/30 rounded-lg px-3">
                        <FaArrowRight className="text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300 group-hover:translate-x-1" />
                        {service.name}
                      </span>
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Company Links */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h2 className="text-xl font-semibold mb-6 text-gray-200 pb-3 border-b border-gray-800 flex items-center gap-2">
                <div className="w-2 h-8 bg-gradient-to-b from-purple-500 to-pink-500 rounded-full" />
                Solutions
              </h2>
              <ul className="space-y-3">
                {solutions.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.05 * index }}
                    whileHover={{ x: 5 }}
                    viewport={{ once: true }}
                    className="group"
                  >
                    <Link href={item.href}>
                      <span className="flex items-center gap-3 text-gray-400 hover:text-white transition-all duration-300 cursor-pointer py-2 group-hover:bg-gray-800/30 rounded-lg px-3">
                        <FaArrowRight className="text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300 group-hover:translate-x-1" />
                        {item.name}
                      </span>
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h2 className="text-xl font-semibold mb-6 text-gray-200 pb-3 border-b border-gray-800 flex items-center gap-2">
                <div className="w-2 h-8 bg-gradient-to-b from-pink-500 to-blue-500 rounded-full" />
                Contact Us
              </h2>
              
              <div className="space-y-6">
                <motion.div 
                  className="flex items-start gap-4 group cursor-pointer"
                  whileHover={{ x: 5 }}
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500/20 to-blue-600/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <FaMapMarkerAlt className="text-blue-400" />
                  </div>
                  <div>
                    <p className="text-gray-300 font-medium mb-1">Address</p>
                    <p className="text-gray-400 text-sm">
                      F-26, Vaishali Nagar,<br />
                      Jaipur, Rajasthan<br />
                      India - 302021
                    </p>
                  </div>
                </motion.div>
                
               <motion.div 
  className="flex items-start gap-4 group cursor-pointer"
  whileHover={{ x: 5 }}
>
  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500/20 to-green-600/20 
                  flex items-center justify-center 
                  group-hover:scale-110 transition-transform duration-300">
    
    <FaPhone className="text-green-400 rotate-[95deg]" />

  </div>

  <div>
    <p className="text-gray-300 font-medium mb-1">Phone</p>
    <a
      href="tel:+917300075187"
      className="text-gray-400 hover:text-white transition-colors duration-300 text-sm"
    >
      +91 73000 75187
    </a>
    <br />
    <span className="mt-2 inline-block text-xs font-semibold uppercase tracking-wider text-gray-500">
      Micro-GCC Discussion
    </span>
    <br />
    <a
      href="tel:+918952012870"
      className="text-gray-400 hover:text-white transition-colors duration-300 text-sm"
    >
      +91 89520 12870
    </a>
  </div>
</motion.div>

                
<motion.div 
  className="flex items-start gap-4 group cursor-pointer"
  whileHover={{ x: 5 }}
>
  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500/20 to-purple-600/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
    <FaEnvelope className="text-purple-400" />
  </div>
  <div>
    <p className="text-gray-300 font-medium mb-1">Email</p>
    <a href="mailto:info@variablesoft.com" className="text-gray-400 hover:text-white transition-colors duration-300 text-sm">
      info@variablesoft.com
    </a>
    {/* Support email below the main email */}
    <br />
    <a href="mailto:support@variablesoft.com" className="text-gray-400 hover:text-white transition-colors duration-300 text-sm">
      support@variablesoft.com
    </a>
  </div>
</motion.div>


              </div>

         <motion.button
  onClick={() => window.open('/getintouch', '_blank', 'noopener,noreferrer')}
  className="mt-8 w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300 relative overflow-hidden group"
  whileHover={{ scale: 1.02 }}
  whileTap={{ scale: 0.98 }}
>
  <span className="relative z-10">Start Your Project</span>
  <motion.div
    className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500"
    initial={{ x: "-100%" }}
    whileHover={{ x: "100%" }}
    transition={{ duration: 0.6 }}
  />
</motion.button>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-12 rounded-2xl border border-gray-800 bg-gray-900/40 px-5 py-5"
          >
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <h2 className="text-base font-semibold text-gray-200">Company</h2>
              <div className="flex flex-wrap gap-3">
                {company.map((item) => (
                  <Link key={item.href} href={item.href}>
                    <span className="inline-flex cursor-pointer rounded-full border border-gray-800 bg-gray-950/40 px-4 py-2 text-sm text-gray-400 transition hover:border-blue-500/60 hover:text-white">
                      {item.name}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Tech Stack Section */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h3 className="text-xl font-semibold mb-6 text-center text-gray-200">
              Technologies We Love
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              {techStack.map((tech, index) => (
                <motion.div
                  key={tech}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ 
                    duration: 0.4, 
                    delay: index * 0.05,
                    type: "spring",
                    stiffness: 200
                  }}
                  whileHover={{ 
                    scale: 1.1,
                    y: -5,
                    backgroundColor: "rgba(59, 130, 246, 0.1)"
                  }}
                  className="px-5 py-3 bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700 hover:border-blue-500 transition-all duration-300 cursor-pointer group"
                  viewport={{ once: true }}
                >
                  <span className="text-gray-200 group-hover:text-white text-sm font-medium">
                    {tech}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Divider */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            transition={{ duration: 1, ease: "easeInOut" }}
            viewport={{ once: true }}
            className="w-full h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent mb-8"
          />

          {/* Bottom Section */}
          <div className="text-center">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-gray-500 mb-6 text-sm"
            >
              Variable Soft is a trade name of{" "}
              <span className="text-gray-300 font-medium">
                "VARIABLE INFOTECH INDIA PRIVATE LIMITED"
              </span>
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex flex-col md:flex-row items-center justify-between pt-8 border-t border-gray-800/50"
            >
              <p className="text-gray-500 text-sm mb-4 md:mb-0">
                © {currentYear} Variable Soft. All rights reserved.
              </p>
              
              <div className="flex items-center gap-6">
                <Link href="/privacy-policy">
                  <span className="text-gray-400 hover:text-white transition-colors duration-300 cursor-pointer text-sm hover:underline">
                    Privacy Policy
                  </span>
                </Link>
                <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
                <Link href="/terms-of-service">
                  <span className="text-gray-400 hover:text-white transition-colors duration-300 cursor-pointer text-sm hover:underline">
                    Terms of Service
                  </span>
                </Link>
              </div>
            </motion.div>

          </div>
        </div>
      </div>
    </footer>
    </>
  );
}
